import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  Platform,
  Dimensions,
  ScrollView,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import LhButton from "../../components/LhButton";
import {
  fetchCategories,
  fetchIssueTypes,
  submitFeedback,
  resetFormStatus,
} from "../../store/slices/contactUsSlice";
import AttachmentUploader from "./AttachmentUploader";
import Svg, { Path } from "react-native-svg";
import ContactUsWelcome from "./ContactUsWelcome";
import LHLoader from "../../components/LHLoader";
import CrossPlatformDropdown from "../../components/CrossPlatformDropdown";
import FontFamily from "../../constants/fonts";

// Define the interface for DropdownItem to match CrossPlatformDropdown's expected props
interface DropdownItem {
  id: string | number;
  displayText: string;
  [key: string]: any;
}

interface ContactUsScreenProps {
  onBackPress?: () => void;
}

const ContactUsScreen: React.FC<ContactUsScreenProps> = () => {
  const dispatch = useDispatch<AppDispatch>();

  // Create our own toast state
  const [toast, setToast] = useState({
    visible: false,
    message: "",
    type: "success" as "success" | "error" | "info" | "warning",
  });

  const {
    categories,
    issueTypes,
    isLoading,
    isSubmitting,
    submitSuccess,
    submitError,
  } = useSelector((state: RootState) => state.contactUs);

  // Get the entire auth state to inspect its structure
  const authState = useSelector((state: RootState) => state.auth);
  console.log("Complete Auth State:", authState);

  // Get user data using the any type to avoid TypeScript errors while debugging
  const userData = useSelector((state: RootState) => {
    const auth = state.auth as any;
    return auth.user?.userData || auth.userData || auth.user || {};
  });
  console.log("Retrieved User Data:", userData);

  const [selectedCategory, setSelectedCategory] = useState<DropdownItem | null>(
    null
  );
  const [selectedIssueType, setSelectedIssueType] =
    useState<DropdownItem | null>(null);
  const [selectedUrgency, setSelectedUrgency] = useState<DropdownItem | null>(
    null
  );
  const [feedback, setFeedback] = useState("");
  // Store the actual file object
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  // Store the filename for display
  const [attachmentFileName, setAttachmentFileName] = useState<string | null>(
    null
  );
  // Store the file URL for cleanup
  const [attachmentFileUrl, setAttachmentFileUrl] = useState<string | null>(
    null
  );

  // Transform API data to appropriate dropdown formats
  const categoryDropdownItems: DropdownItem[] = categories.map((cat) => ({
    id: cat.ID ?? -1,
    displayText: cat.Name || "Unknown",
  }));

  const issueTypeDropdownItems: DropdownItem[] = issueTypes.map((issue) => ({
    id: issue.ID ?? -1,
    displayText: issue.Name || "Unknown",
  }));

  const urgencyOptions: DropdownItem[] = [
    { id: "High", displayText: "High" },
    { id: "Medium", displayText: "Medium" },
    { id: "Low", displayText: "Low" },
  ];

  useEffect(() => {
    // Fetch categories and issue types on component mount
    dispatch(fetchCategories());
    dispatch(fetchIssueTypes());

    // Clean up on unmount
    return () => {
      // Clean up file URL if it exists (for web platform)
      if (attachmentFileUrl && Platform.OS === "web") {
        URL.revokeObjectURL(attachmentFileUrl);
      }
      dispatch(resetFormStatus());
    };
  }, [dispatch, attachmentFileUrl]);

  // Function to show toast messages
  const showToast = (
    message: string,
    type: "success" | "error" | "info" | "warning"
  ) => {
    setToast({ visible: true, message, type });

    // Hide toast after 3 seconds
    setTimeout(() => {
      setToast({ visible: false, message: "", type: "success" });
    }, 3000);
  };

  useEffect(() => {
    // Show success or error toast when submission status changes
    if (submitSuccess) {
      showToast("Feedback submitted successfully", "success");
      // Reset form and clean up attachments after successful submission
      resetForm();
    }

    if (submitError) {
      showToast("Failed to submit feedback. Please try again.", "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitSuccess, submitError]);

  const resetForm = () => {
    setSelectedCategory(null);
    setSelectedIssueType(null);
    setSelectedUrgency(null);
    setFeedback("");

    // Clean up file URL if it exists (for web platform)
    if (attachmentFileUrl && Platform.OS === "web") {
      URL.revokeObjectURL(attachmentFileUrl);
      console.log("Cleaned up file URL after form reset");
    }

    setAttachmentFile(null);
    setAttachmentFileName(null);
    setAttachmentFileUrl(null);
    dispatch(resetFormStatus());
  };

  const handleSubmit = () => {
    if (
      !selectedCategory ||
      !selectedIssueType ||
      !selectedUrgency ||
      !feedback
    ) {
      showToast("Please fill all required fields", "error");
      return;
    }

    // Get the username or email from userData, with fallbacks
    const userIdentifier =
      (userData as any)?.username ||
      (userData as any)?.userPrincipalName ||
      (userData as any)?.email ||
      "anonymous";

    // Create FormData object for multipart/form-data submission
    const formData = new FormData();
    formData.append("CategoryID", selectedCategory.id.toString());
    formData.append("IssueTypeID", selectedIssueType.id.toString());
    formData.append("Urgency", selectedUrgency.id.toString());
    formData.append("Feedback", feedback);
    formData.append("Created_By", userIdentifier);

    // Append file if it exists
    if (attachmentFile) {
      formData.append("attachment", attachmentFile);
    }

    console.log(
      "Submitting feedback with file attachment:",
      attachmentFile?.name
    );
    // Dispatch action with FormData
    dispatch(submitFeedback({ formData }));
  };

  // Detect iOS phone specifically (not iPad) for single-column layout
  const isIosPhone = () => {
    const windowWidth = Dimensions.get("window").width;
    return Platform.OS === "ios" && windowWidth < 768;
  };

  const handleAttachmentUpload = (file: File | null, fileUrl?: string) => {
    // Clean up previous file URL if it exists (for web platform)
    if (attachmentFileUrl && Platform.OS === "web") {
      URL.revokeObjectURL(attachmentFileUrl);
    }

    if (file) {
      setAttachmentFile(file);
      setAttachmentFileName(file.name);
      setAttachmentFileUrl(fileUrl || null);
      console.log("File selected:", file.name);
    } else {
      setAttachmentFile(null);
      setAttachmentFileName(null);
      setAttachmentFileUrl(null);
    }
  };

  const handleCategoryChange = (
    value: DropdownItem | DropdownItem[] | null
  ) => {
    // If multiple values are selected (array), use the first one, otherwise use the single value
    const item = Array.isArray(value) ? value[0] : value;
    setSelectedCategory(item);
  };

  const handleIssueTypeChange = (
    value: DropdownItem | DropdownItem[] | null
  ) => {
    const item = Array.isArray(value) ? value[0] : value;
    setSelectedIssueType(item);
  };

  const handleUrgencyChange = (value: DropdownItem | DropdownItem[] | null) => {
    const item = Array.isArray(value) ? value[0] : value;
    setSelectedUrgency(item);
  };

  if (isLoading) {
    return <LHLoader testID="LHLoader" />;
  }

  return (
    <ScrollView style={styles.container}>
      <ContactUsWelcome />

      {toast.visible && (
        <View
          style={[
            styles.toastContainer,
            toast.type === "success"
              ? styles.successToast
              : toast.type === "error"
              ? styles.errorToast
              : toast.type === "warning"
              ? styles.warningToast
              : styles.infoToast,
          ]}
        >
          <Text style={styles.toastText}>{toast.message}</Text>
        </View>
      )}

      <View style={styles.formCard}>
        <View style={styles.formContent}>
          <View
            style={isIosPhone() ? styles.formRowMobile : styles.formRowThreeCol}
          >
            <View
              style={isIosPhone() ? styles.fullWidth : styles.formFieldThreeCol}
            >
              <View style={styles.labelWrapper}>
                <Text style={styles.label}>Category</Text>
                <Text style={styles.requiredLabelTopRight}>*Required</Text>
              </View>
              <CrossPlatformDropdown
                childList={categoryDropdownItems}
                placeholder="Category/Section"
                selectedValue={selectedCategory}
                setSelectedValue={handleCategoryChange}
                disableSorting={true} // Disable sorting for categories
              />
            </View>

            <View
              style={isIosPhone() ? styles.fullWidth : styles.formFieldThreeCol}
            >
              <View style={styles.labelWrapper}>
                <Text style={styles.label}>Issue Type</Text>
                <Text style={styles.requiredLabelTopRight}>*Required</Text>
              </View>
              <CrossPlatformDropdown
                childList={issueTypeDropdownItems}
                placeholder="Type of issue"
                selectedValue={selectedIssueType}
                setSelectedValue={handleIssueTypeChange}
              />
            </View>

            <View
              style={isIosPhone() ? styles.fullWidth : styles.formFieldThreeCol}
            >
              <View style={styles.labelWrapper}>
                <Text style={styles.label}>Urgency</Text>
                <Text style={styles.requiredLabelTopRight}>*Required</Text>
              </View>
              <CrossPlatformDropdown
                childList={urgencyOptions}
                placeholder="Urgency level"
                selectedValue={selectedUrgency}
                setSelectedValue={handleUrgencyChange}
              />
            </View>
          </View>

          {isIosPhone() ? (
            <>
              <View style={styles.feedbackContainer}>
                <View style={styles.labelWrapper}>
                  <Text style={styles.label}>Feedback</Text>
                  <Text style={styles.requiredLabelTopRight}>*Required</Text>
                </View>
                <TextInput
                  style={[styles.feedbackInput, styles.feedbackInputTall]}
                  value={feedback}
                  onChangeText={setFeedback}
                  placeholder="Type here"
                  placeholderTextColor="#868e96"
                  multiline
                  numberOfLines={6}
                />
              </View>

              <View style={styles.attachmentLabelRow}>
                <Text style={styles.label}>Attachments</Text>
                <Svg
                  width={14}
                  height={14}
                  viewBox="0 0 14 14"
                  style={styles.infoIcon}
                >
                  <Path
                    d="M6.33334 3.66668H7.66668V5.00001H6.33334V3.66668ZM6.33334 6.33334H7.66668V10.3333H6.33334V6.33334ZM7.00001 0.333344C3.32001 0.333344 0.333344 3.32001 0.333344 7.00001C0.333344 10.68 3.32001 13.6667 7.00001 13.6667C10.68 13.6667 13.6667 10.68 13.6667 7.00001C13.6667 3.32001 10.68 0.333344 7.00001 0.333344ZM7.00001 12.3333C4.06001 12.3333 1.66668 9.94001 1.66668 7.00001C1.66668 4.06001 4.06001 1.66668 7.00001 1.66668C9.94001 1.66668 12.3333 4.06001 12.3333 7.00001C12.3333 9.94001 9.94001 12.3333 7.00001 12.3333Z"
                    fill="#3A3A3A"
                  />
                </Svg>
              </View>
              <View style={styles.attachmentContainer}>
                <AttachmentUploader
                  onFileUpload={handleAttachmentUpload}
                  currentFileName={attachmentFileName}
                />
              </View>
            </>
          ) : (
            <View style={styles.twoColRow}>
              <View style={styles.formFieldThreeCol}>
                <View style={styles.labelWrapper}>
                  <Text style={styles.label}>Feedback</Text>
                  <Text style={styles.requiredLabelTopRight}>*Required</Text>
                </View>
                <TextInput
                  style={[styles.feedbackInput, styles.feedbackInputTall]}
                  value={feedback}
                  onChangeText={setFeedback}
                  placeholder="Type here"
                  placeholderTextColor="#868e96"
                  multiline
                  numberOfLines={6}
                />
              </View>

              <View style={styles.formFieldThreeCol}>
                <View style={styles.attachmentLabelRow}>
                  <Text style={styles.label}>Attachments</Text>
                  <Svg
                    width={14}
                    height={14}
                    viewBox="0 0 14 14"
                    style={styles.infoIcon}
                  >
                    <Path
                      d="M6.33334 3.66668H7.66668V5.00001H6.33334V3.66668ZM6.33334 6.33334H7.66668V10.3333H6.33334V6.33334ZM7.00001 0.333344C3.32001 0.333344 0.333344 3.32001 0.333344 7.00001C0.333344 10.68 3.32001 13.6667 7.00001 13.6667C10.68 13.6667 13.6667 10.68 13.6667 7.00001C13.6667 3.32001 10.68 0.333344 7.00001 0.333344ZM7.00001 12.3333C4.06001 12.3333 1.66668 9.94001 1.66668 7.00001C1.66668 4.06001 4.06001 1.66668 7.00001 1.66668C9.94001 1.66668 12.3333 4.06001 12.3333 7.00001C12.3333 9.94001 9.94001 12.3333 7.00001 12.3333Z"
                      fill="#3A3A3A"
                    />
                  </Svg>
                </View>
                <View style={styles.attachmentContainer}>
                  <AttachmentUploader
                    onFileUpload={handleAttachmentUpload}
                    currentFileName={attachmentFileName}
                  />
                </View>
              </View>
            </View>
          )}

          <View style={styles.buttonContainer}>
            <LhButton
              text="Submit Feedback"
              variant="primary"
              size="medium"
              onPress={handleSubmit}
              isLoading={isSubmitting}
              disabled={isSubmitting}
              style={styles.submitButton}
            />
          </View>
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: Platform.OS === "web" ? "5%" : 20,
    backgroundColor: "#F3F7FD",
    paddingVertical: Platform.OS === "web" ? "1.5%" : 24,
  },
  toastContainer: {
    padding: 12,
    borderRadius: 4,
    marginBottom: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  successToast: {
    backgroundColor: "#4caf50",
  },
  errorToast: {
    backgroundColor: "#f44336",
  },
  warningToast: {
    backgroundColor: "#ff9800",
  },
  infoToast: {
    backgroundColor: "#2196f3",
  },
  toastText: {
    color: "white",
    fontSize: 16,
    fontFamily: FontFamily.regular
  },
  formCard: {
    backgroundColor: "white",
    borderRadius: 8,
    marginTop: 16,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
    overflow: "hidden",
    width: "100%",
  },
  formHeader: {
    backgroundColor: "#0066CC",
    padding: Platform.OS === "web" ? 16 : 20,
  },
  formTitle: {
    color: "white",
    fontSize: Platform.OS === "web" ? 18 : 20,
    fontWeight: "600",
  },
  formContent: {
    padding: Platform.OS === "web" ? 24 : 24,
  },
  formRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 16,
    gap: 16,
  },
  formRowThreeCol: {
    flexDirection: "row",
    flexWrap: "nowrap",
    marginBottom: 16,
    justifyContent: "space-between",
    gap: 16,
  },
  formFieldThreeCol: {
    flexBasis: "33.3333%",
    maxWidth: "32%",
    minWidth: 0,
  },
  twoColRow: {
    flexDirection: "row",
    gap: 16,
    justifyContent: "flex-start",
    marginBottom: 16,
  },
  formRowMobile: {
    flexDirection: "column",
    marginBottom: 20,
    gap: Platform.OS === "web" ? 16 : 20,
  },
  formField: {
    flex: 1,
    minWidth: 250,
  },
  fullWidth: {
    width: "100%",
  },
  label: {
    fontSize: Platform.OS === "web" ? 12 : 12,
    fontWeight: "400",
    color: "#3A3A3A",
    marginBottom: Platform.OS === "web" ? 8 : 10,
    fontFamily: FontFamily.regular
  },
  requiredStar: {
    color: "red",
  },
  feedbackContainer: {
    marginBottom: 16,
  },
  feedbackInput: {
    borderWidth: 1,
    borderColor: "#ced4da",
    borderRadius: 8,
    padding: Platform.OS === "web" ? 12 : 16,
    fontSize: Platform.OS === "web" ? 16 : 18,
    backgroundColor: "#fff",
    textAlignVertical: "top",
    minHeight: Platform.OS === "web" ? 120 : 150,
    fontFamily: FontFamily.regular
  },
  feedbackInputTall: {
    height: 134,
  },
  attachmentContainer: {
    marginBottom: Platform.OS === "web" ? 24 : 30,
    // Ensure the attachment area keeps a consistent visual height so
    // showing or hiding the file preview doesn't cause the form to reflow
    // and move the submit button unexpectedly.
    minHeight: Platform.OS === "web" ? 80 : 90,
    // Figma: rounded dashed container with light blue background
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: "#D7E5F8",
    backgroundColor: "#EFF5FC",
    paddingHorizontal: 16,
    paddingVertical: Platform.OS === "web" ? 12 : 28,
    alignItems: "center",
    overflow: "hidden",
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent:
      Platform.OS === "ios" && Dimensions.get("window").width < 768
        ? "center"
        : "flex-start",
    marginTop: Platform.OS === "web" ? 0 : 10,
  },
  submitButton: {
    // Emulate inline-flex on web by keeping the button sized to content
    alignSelf: Platform.OS === "web" ? "flex-start" : "center",
    width:
      Platform.OS === "ios" && Dimensions.get("window").width < 768
        ? "100%"
        : "15%",
    minWidth: Platform.OS === "web" ? 150 : 180,
    height: Platform.OS === "ios" ? 50 : undefined,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
    borderRadius: 24,
    backgroundColor: "#005CBC",
    paddingHorizontal: 4,
    // Remove negative top margin which caused the button to overlap and
    // shift when adjacent content size changed. Use regular spacing in
    // the container instead.
    marginTop: Platform.OS === "web" ? 8 : 10,
  },
  labelWrapper: {
    position: "relative",
    paddingRight: 12, // reduced to avoid extra horizontal space
  },
  requiredLabelTopRight: {
    position: "absolute",
    right: 0,
    top: 2,
    color: "#616161",
    textAlign: "right",
    fontSize: 10,
    fontStyle: "normal",
    fontWeight: "500",
    lineHeight: 14,
    fontFamily: FontFamily.medium
  },
  attachmentLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    // Add extra top spacing only on iOS phones so the label aligns like other labels on narrow screens
    marginTop:
      Platform.OS === "ios" && Dimensions.get("window").width < 768 ? 16 : 0,
  },
  infoIcon: {
    marginLeft: 10,
    marginTop: -8,
  },
});

export default ContactUsScreen;
